import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { PollService, type Poll, type PollFilter, type PollVoteRow, type QuestionStats } from "#gen/forms/v1/poll_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

interface PollList {
	polls: Poll[];
	pageInfo?: PaginationPageInfo;
}

interface PollVoteList {
	votes: PollVoteRow[];
	pageInfo?: PaginationPageInfo;
}

export class PollsClient {
	private client: Client<typeof PollService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.polls || "/", PollService, options);
	}

	async getPoll(id: string): Promise<[Poll, QuestionStats[]]> {
		try {
			const res = await this.client.getPoll({ id });
			return [res.poll!, res.questionStats!];
		} catch (e) {
			throw handleError(e);
		}
	}

	async listPolls(orgId: string, workspaceId: string, filter?: PollFilter, pagination?: PaginationArguments, options?: CallOptions): Promise<PollList> {
		try {
			const res = await this.client.listPolls(
				{
					orgId,
					workspaceId,
					filter,
					pagination,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listPollVotes(pollId: string, pagination?: PaginationArguments, options?: CallOptions): Promise<PollVoteList> {
		try {
			const res = await this.client.listPollVotes(
				{
					pollId,
					pagination,
				},
				options,
			);
			return {
				votes: res.votes ?? [],
				pageInfo: res.pageInfo,
			};
		} catch (e) {
			throw handleError(e);
		}
	}

	async submitAnswer(pollId: string, questionId: string, sessionId: string, submitterId: string, value: string): Promise<QuestionStats> {
		try {
			const res = await this.client.submitPollAnswer({ pollId, questionId, sessionId, value, submitterId });
			return res.questionStats!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getPollStats(pollId: string, sessionId: string, submitterId: string): Promise<QuestionStats[]> {
		try {
			const res = await this.client.getPollStats({ pollId, sessionId, submitterId });
			return res.questionStats!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getQuestionStats(pollId: string, questionId: string, sessionId: string, submitterId: string): Promise<QuestionStats> {
		try {
			const res = await this.client.getQuestionStats({ pollId, questionId, sessionId, submitterId });
			return res.questionStats!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createPoll(page: string, poll: Poll): Promise<Poll> {
		try {
			const res = await this.client.createPoll({
				orgId: poll.orgId,
				workspaceId: poll.workspaceId,
				title: poll.title,
				description: poll.description,
				questions: poll.questions,
				opaqueId: page,
			});
			return res.poll!;
		} catch (e) {
			throw handleError(e);
		}
	}
}
